import React, { useCallback, useMemo, useState } from "react";
import { connect } from "react-redux";

import { faTrash } from "@fortawesome/pro-solid-svg-icons/faTrash";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import Badge from "antd/es/badge";
import Tooltip from "antd/es/tooltip";
import cn from "classnames";
import { defineMessages, useIntl } from "react-intl";

import { EntityType, EntityTypesSupportingChildLayouts } from "@mapmycustomers/shared/types/entity";
import ActivityType from "@mapmycustomers/shared/types/entity/activities/ActivityType";
import Funnel from "@mapmycustomers/shared/types/entity/deals/Funnel";
import Stage from "@mapmycustomers/shared/types/entity/deals/Stage";
import { ChildLayout } from "@mapmycustomers/shared/types/layout/FormLayout";
import { stopEvents } from "@mapmycustomers/shared/util/browser";
import { Button, Popconfirm } from "@mapmycustomers/ui";

import ActivityTypeIcon from "@app/component/activity/ActivityTypeIcon";
import { getActivityTypes } from "@app/store/activity";
import { getFunnelStages } from "@app/store/deal";
import { RootState } from "@app/store/rootReducer";
import colors from "@app/styles/_colors.module.scss";
import anyColorToHex from "@app/util/colors/anyColorToHex";

import {
  getActiveEntityType,
  getEditedChildLayoutId,
  getEditedLayoutDefaultCheckInVariant,
} from "../../store";
import { deleteChildLayout, selectChildLayout } from "../../store/actions";
import DefaultCheckInVariant from "../../store/DefaultCheckInVariant";
import DefaultCheckInVariantTag from "../DefaultCheckInVariantTag";

import styles from "./VariantItem.module.scss";

const messages = defineMessages({
  deleteText: {
    id: "settings.formLayouts.modal.variants.item.delete.text",
    defaultMessage: "Are you sure you want to delete this variant?",
    description: "Variant delete confirmation text",
  },
  deleteTooltip: {
    id: "settings.formLayouts.modal.variants.item.delete.tooltip",
    defaultMessage: "Delete Variant",
    description: "Variant delete button tooltip text",
  },
  [EntityType.ACTIVITY]: {
    id: "settings.formLayouts.modal.variants.item.delete.activity",
    defaultMessage: "Users will see the primary layout when using this type.",
    description: "Variant delete confirmation for activities layouts",
  },
  [EntityType.DEAL]: {
    id: "settings.formLayouts.modal.variants.item.delete.deals",
    defaultMessage: "Users will see the primary layout when using this stage.",
    description: "Variant delete confirmation text for deals layouts",
  },
  wait: {
    id: "settings.formLayouts.modal.variants.item.delete.wait",
    defaultMessage: "Wait",
    description: "Delete action - wait",
  },
  yes: {
    id: "settings.formLayouts.modal.variants.item.delete.yes",
    defaultMessage: "Yes, Delete",
    description: "Delete action - yes",
  },
});

interface Props {
  activeChildLayout?: ChildLayout["id"];
  activityTypes: ActivityType[];
  childLayout: ChildLayout;
  defaultCheckInVariant?: DefaultCheckInVariant;
  deleteChildLayout: typeof deleteChildLayout;
  entityType: EntityTypesSupportingChildLayouts;
  funnelStages: Record<Funnel["id"], Stage[]>;
  selectChildLayout: typeof selectChildLayout;
}

const VariantItem: React.FC<Props> = ({
  activeChildLayout,
  activityTypes,
  childLayout,
  defaultCheckInVariant,
  deleteChildLayout,
  entityType,
  funnelStages,
  selectChildLayout,
}) => {
  const intl = useIntl();

  const [visible, setVisible] = useState(false);
  const isActivity = entityType === EntityType.ACTIVITY;
  const isDeal = entityType === EntityType.DEAL;
  const isActive = activeChildLayout === childLayout.id;
  const isDefaultCheckInVariant =
    defaultCheckInVariant &&
    !defaultCheckInVariant.isPrimary &&
    defaultCheckInVariant.id === childLayout.id;

  const activityType = useMemo(() => {
    if (isActivity && childLayout.crmActivityType?.id) {
      return (activityTypes ?? []).find((type) => type.id === childLayout.crmActivityType!.id);
    }

    return undefined;
  }, [activityTypes, childLayout, isActivity]);

  const stage = useMemo(() => {
    if (isDeal && childLayout.funnel?.id && childLayout.stage?.id) {
      const funnel = funnelStages[childLayout.funnel.id];
      return funnel?.find((stage) => stage.id === childLayout.stage!.id);
    }

    return undefined;
  }, [childLayout, funnelStages, isDeal]);
  const stageColor = stage?.color ? anyColorToHex(stage.color) : colors.slate;

  const handleClick = useCallback(
    () => selectChildLayout(childLayout.id),
    [childLayout.id, selectChildLayout]
  );
  const handleDelete = useCallback(
    () => deleteChildLayout(childLayout.id),
    [childLayout.id, deleteChildLayout]
  );
  const handleVisibleChange = useCallback((visible: boolean) => setVisible(visible), [setVisible]);

  const deleteButton = (
    <Button danger icon={<FontAwesomeIcon icon={faTrash} />} iconOnly type="link" />
  );

  return (
    <Button
      className={cn(styles.btn, {
        [styles.active]: isActive,
        [styles.hover]: !isActive && visible,
      })}
      onClick={handleClick}
    >
      <Tooltip mouseLeaveDelay={0} placement="top" title={stage?.funnel.name}>
        <div className={styles.name}>
          <div className={styles.icon}>
            {isDeal && <Badge color={stageColor} />}

            {isActivity && activityType && (
              <ActivityTypeIcon
                activityType={activityType}
                className={styles.type}
                size="md"
                tooltipProps={false}
              />
            )}
          </div>

          <div className={styles.value}>{isDeal ? stage?.name : activityType?.name}</div>

          {isDefaultCheckInVariant && <DefaultCheckInVariantTag />}
        </div>
      </Tooltip>

      <div className={styles.action} onClick={stopEvents}>
        <Popconfirm
          arrowPointAtCenter
          cancelText={intl.formatMessage(messages.wait)}
          description={intl.formatMessage(messages[entityType])}
          okText={intl.formatMessage(messages.yes)}
          onConfirm={handleDelete}
          onOpenChange={handleVisibleChange}
          overlayClassName={styles.overlay}
          placement="bottomRight"
          title={intl.formatMessage(messages.deleteText)}
        >
          {visible ? (
            deleteButton
          ) : (
            <Tooltip
              arrowPointAtCenter
              mouseLeaveDelay={0}
              placement="topRight"
              title={intl.formatMessage(messages.deleteTooltip)}
              trigger={["hover"]}
            >
              {deleteButton}
            </Tooltip>
          )}
        </Popconfirm>
      </div>
    </Button>
  );
};

const mapStateToProps = (state: RootState) => ({
  activeChildLayout: getEditedChildLayoutId(state),
  activityTypes: getActivityTypes(state),
  defaultCheckInVariant: getEditedLayoutDefaultCheckInVariant(state),
  entityType: getActiveEntityType(state) as EntityTypesSupportingChildLayouts,
  funnelStages: getFunnelStages(state),
});

const mapDispatchToProps = {
  deleteChildLayout,
  selectChildLayout,
};

export default connect(mapStateToProps, mapDispatchToProps)(VariantItem);
